// =============================================================================
// RubricPicker.tsx
// =============================================================================
// Fase 2.6 Blokk G — Gjenbrukbar rubric-velger som fetcher assessment_rubrics
// filtrert etter brukerens medlemstilhørighet. Brukes i ExamCreator og
// (framtidig) LmsMappingModal når studieplanens framework krever rubric.
//
// Oppfører seg som en kontrollert komponent — forelder eier `value` og
// `onChange`. Null = ingen rubric valgt (has_rubric utledes til false).
// =============================================================================

import { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, AlertCircle, Lock } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

// Norske labels for enum-verdier — speilet fra RubricManagement + CompositePlanEditor
const COMPLEXITY_LABELS: Record<string, string> = {
  simple: 'enkel',
  graded: 'gradert',
  advanced: 'avansert',
};
const COMPONENT_TYPE_LABELS: Record<string, string> = {
  written: 'skriftlig prøve',
  project: 'prosjektinnlevering',
  oral: 'muntlig',
  practical: 'praktisk demonstrasjon',
  portfolio: 'mappe',
  observation: 'observasjon',
  reflection: 'refleksjonsnotat',
  group: 'gruppearbeid',
};

interface Rubric {
  id: string;
  title: string;
  description: string | null;
  version_number: number;
  is_locked: boolean;
  complexity_mode: 'simple' | 'graded' | 'advanced';
  applicable_component_types: string[];
  member_id: string | null;
  member_name: string | null;
}

interface Props {
  /** Valgt rubric-id. null = ingen rubric valgt */
  value: string | null;
  /** Kalles når bruker endrer valg */
  onChange: (rubricId: string | null) => void;
  /** Når true: ramme krever rubric (vises prominent med rød border hvis ikke valgt) */
  required?: boolean;
  /** Hvis satt, filtrer rubrics etter om applicable_component_types inneholder denne */
  filterByComponentType?: 'written' | 'practical' | 'project' | 'oral' | 'observation';
  /** Deaktiver (f.eks. ved submit) */
  disabled?: boolean;
}

export default function RubricPicker({
  value,
  onChange,
  required = false,
  filterByComponentType,
  disabled = false,
}: Props) {
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch('/api/admin/assessment-rubrics');
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setError(data.error || 'Kunne ikke hente vurderings-rubrics');
          setRubrics([]);
        } else {
          const list: Rubric[] = Array.isArray(data.rubrics) ? data.rubrics : [];
          setRubrics(list);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message);
          setRubrics([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filtrer etter component-type (hvis satt)
  const filteredRubrics = filterByComponentType
    ? rubrics.filter(r => r.applicable_component_types.includes(filterByComponentType))
    : rubrics;

  const selectedRubric = value ? rubrics.find(r => r.id === value) : null;
  const missingRequired = required && !value;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 p-3 border border-gray-200 rounded-md">
        <Loader2 size={14} className="animate-spin" />
        Henter vurderings-rubrics…
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${required ? 'border-l-4 pl-3 ' + (missingRequired ? 'border-red-400' : 'border-green-400') : ''}`}>
      <label className="block text-sm font-medium text-gray-700">
        <ClipboardCheck size={14} className="inline mr-1" />
        Vurderings-rubric {required && <span className="text-red-600">*</span>}
      </label>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-700 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className={`w-full border rounded-md p-2 text-sm bg-white ${
          missingRequired ? 'border-red-300' : 'border-gray-300'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <option value="">— Ingen rubric valgt —</option>
        {filteredRubrics.length === 0 && (
          <option disabled>Ingen tilgjengelige rubrics for din organisasjon</option>
        )}
        {filteredRubrics.map((r) => (
          <option key={r.id} value={r.id}>
            {r.title}
            {r.is_locked ? ' 🔒' : ''}
            {` (v${r.version_number})`}
            {r.member_name && ` — ${r.member_name}`}
          </option>
        ))}
      </select>

      {selectedRubric && (
        <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1">
          <div className="flex items-center gap-2">
            {selectedRubric.is_locked && <Lock size={12} className="text-amber-600" />}
            <span className="font-medium">{selectedRubric.title}</span>
            <span className="text-gray-500">v{selectedRubric.version_number}</span>
          </div>
          {selectedRubric.description && (
            <div className="text-gray-500 line-clamp-2">{selectedRubric.description}</div>
          )}
          <div className="text-gray-500">
            Nivå: {COMPLEXITY_LABELS[selectedRubric.complexity_mode] || selectedRubric.complexity_mode}
            {' · '}
            Brukes for: {selectedRubric.applicable_component_types
              .map(t => COMPONENT_TYPE_LABELS[t] || t)
              .join(', ')}
          </div>
        </div>
      )}

      {missingRequired && (
        <div className="text-xs text-red-700 flex items-start gap-1">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
          Studieplanens vurderingsramme krever at prøvesettet er koblet til en rubric. Velg en fra listen eller opprett en ny i Innstillinger → Vurderings-rubrics.
        </div>
      )}

      {!required && !value && filteredRubrics.length > 0 && (
        <div className="text-xs text-gray-500">
          Valgfri — kobles kun hvis studieplanens vurderingsramme krever strukturert rubric.
        </div>
      )}
    </div>
  );
}
